import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Brain,
  Shield,
  TrendingUp,
  Users,
  FileText,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Loader2,
  Clock,
} from "lucide-react";

// Definition-of-record for the 5 agents. Order matters — this is the chat feed order.
const AGENTS = [
  { role: "research", name: "Research Agent", icon: Brain, tagline: "Analyzing job description & mapping competencies" },
  { role: "cxo", name: "CXO Agent", icon: TrendingUp, tagline: "Adding strategic & leadership perspective" },
  { role: "cybersecurity", name: "Cybersecurity Expert", icon: Shield, tagline: "Generating technical security questions" },
  { role: "sales", name: "Sales Agent", icon: Users, tagline: "Creating sales & communication scenarios" },
  { role: "test_creator", name: "Test Creator", icon: FileText, tagline: "Synthesizing the final assessment" },
];

function agentStatus(pipelineStatus, role) {
  if (!pipelineStatus) return "pending";
  const traces = pipelineStatus.agent_traces || [];
  const trace = traces.find((t) => t.agent === role);
  if (trace?.status === "completed") return "completed";
  if (pipelineStatus.current_agent === role) return "in_progress";
  return "pending";
}

function StatusBadge({ status }) {
  if (status === "completed") {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-300 gap-1">
        <CheckCircle className="w-3 h-3" /> done
      </Badge>
    );
  }
  if (status === "in_progress") {
    return (
      <Badge className="bg-blue-100 text-blue-700 border-blue-300 gap-1">
        <Loader2 className="w-3 h-3 animate-spin" /> thinking…
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-gray-500 gap-1">
      <Clock className="w-3 h-3" /> queued
    </Badge>
  );
}

/**
 * One chat bubble per agent. Auto-expands the *active* agent; collapses when done
 * (user can re-expand any agent to inspect its reasoning).
 */
function AgentBubble({ agent, status, trace, autoExpanded, onToggle, expanded }) {
  const Icon = agent.icon;
  const thinking = trace?.thinking || null;

  const borderClass =
    status === "completed"
      ? "border-green-300"
      : status === "in_progress"
      ? "border-blue-300 shadow-md"
      : "border-gray-200";

  return (
    <div
      className={`bg-white rounded-xl border ${borderClass} transition-all`}
      data-testid={`agent-bubble-${agent.role}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 rounded-xl"
        data-testid={`agent-bubble-toggle-${agent.role}`}
      >
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            status === "completed"
              ? "bg-green-500 text-white"
              : status === "in_progress"
              ? "bg-primary text-white"
              : "bg-gray-100 text-gray-400"
          }`}
        >
          {status === "in_progress" ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Icon className="w-4 h-4" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-gray-900">{agent.name}</span>
            <StatusBadge status={status} />
          </div>
          <p className="text-xs text-gray-500 truncate">
            {trace?.output_summary || agent.tagline}
          </p>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100" data-testid={`agent-bubble-body-${agent.role}`}>
          {status === "pending" && (
            <p className="text-xs text-gray-500 py-3 italic">
              Waiting for upstream agents to finish…
            </p>
          )}

          {status === "in_progress" && !thinking && (
            <div className="flex items-center gap-2 text-sm text-blue-700 py-3">
              <Loader2 className="w-4 h-4 animate-spin" />
              {agent.tagline}…
            </div>
          )}

          {thinking && (
            <div className="space-y-3 pt-3">
              {thinking.headline && (
                <p className="text-sm font-medium text-gray-800">{thinking.headline}</p>
              )}

              {thinking.bullets && thinking.bullets.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Preview ({thinking.bullets.length})
                  </p>
                  <ul className="space-y-1.5">
                    {thinking.bullets.map((b, i) => (
                      <li
                        key={`${agent.role}-b-${i}`}
                        className="text-sm text-gray-700 bg-gray-50 rounded px-3 py-2 leading-snug"
                      >
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {thinking.insights && thinking.insights.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                    Insights
                  </p>
                  <ul className="space-y-1 text-xs text-gray-600">
                    {thinking.insights.map((i, idx) => (
                      <li key={`${agent.role}-i-${idx}`}>• {i}</li>
                    ))}
                  </ul>
                </div>
              )}

              {trace?.timestamp && (
                <p className="text-[11px] text-gray-400 font-mono">
                  ⏱ {new Date(trace.timestamp).toLocaleTimeString()}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* autoExpanded is a hint to the parent but has no visual effect itself */}
      <span className="sr-only" data-auto-expanded={autoExpanded ? "true" : "false"} />
    </div>
  );
}

export default function AgentChatStream({ pipelineStatus }) {
  // Per-agent expanded state. We AUTO-EXPAND the active agent; the user can
  // override with a manual toggle, which we track in `overrides`.
  const [overrides, setOverrides] = useState({}); // role -> bool (user-forced)
  const bottomRef = useRef(null);

  const currentAgent = pipelineStatus?.current_agent;

  // Autoscroll as agents arrive.
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [pipelineStatus?.current_agent, pipelineStatus?.agent_traces?.length]);

  const toggle = (role) =>
    setOverrides((prev) => {
      const traces = pipelineStatus?.agent_traces || [];
      const trace = traces.find((t) => t.agent === role);
      const status =
        trace?.status === "completed"
          ? "completed"
          : currentAgent === role
          ? "in_progress"
          : "pending";
      const autoExpanded = status === "in_progress";
      // If there's no override yet, derive from auto-expansion and flip.
      const wasOpen = prev[role] ?? autoExpanded;
      return { ...prev, [role]: !wasOpen };
    });

  return (
    <div className="space-y-3" data-testid="agent-chat-stream">
      <div className="bg-gradient-to-br from-primary/5 to-transparent border border-primary/20 rounded-xl p-4">
        <p className="text-sm text-gray-700">
          <span className="font-semibold text-primary">AI Pipeline</span> · 5 specialist agents
          are collaborating on your test. Click any agent below to inspect its reasoning.
        </p>
      </div>

      {AGENTS.map((agent) => {
        const status = agentStatus(pipelineStatus, agent.role);
        const trace = (pipelineStatus?.agent_traces || []).find((t) => t.agent === agent.role);
        const autoExpanded = status === "in_progress";
        const expanded = overrides[agent.role] ?? autoExpanded;
        return (
          <AgentBubble
            key={agent.role}
            agent={agent}
            status={status}
            trace={trace}
            autoExpanded={autoExpanded}
            expanded={expanded}
            onToggle={() => toggle(agent.role)}
          />
        );
      })}

      <div ref={bottomRef} />
    </div>
  );
}
